/**
 * Legal content.
 *
 * WHY THE BODY TEXT IS ENGLISH
 *
 * A privacy policy and terms are legal instruments, and a mistranslation is a
 * liability rather than a courtesy. The interface is fully localised; the body of
 * the legal documents is provided in the authoritative English text, with a
 * translated notice on every locale saying so. Translating the body requires review
 * by someone qualified to do it — an operational task, not something to guess at in
 * code.
 *
 * Everything below describes what ITA6 ACTUALLY does. Each claim maps to something
 * in the repository: the tables in `migrations/`, the monitoring in
 * `worker/crypto/monitor.ts`, the payout rules in `worker/services/cryptoPayouts.ts`,
 * and the ledger in `worker/db/ledger.ts`. Nothing here is aspirational.
 */

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDocument {
  titleKey: 'legal.privacy' | 'legal.terms';
  lastUpdated: string;
  intro: string;
  sections: LegalSection[];
}

const LAST_UPDATED = '2026-10-06';

export const PRIVACY_POLICY: LegalDocument = {
  titleKey: 'legal.privacy',
  lastUpdated: LAST_UPDATED,
  intro:
    'This policy explains what ITA6 collects, why, how long it is kept, and what we do not do. It covers the contest platform, the account system and the crypto payment rail.',
  sections: [
    {
      heading: '1. Who we are',
      paragraphs: [
        'ITA6 operates a skill-based contest platform. Contests are entered with USDT on a public blockchain, and results are decided by objective, timed questions scored on the server.',
      ],
    },
    {
      heading: '2. What we collect',
      bullets: [
        'Account data: your email address, a password hash (never the password), your role, your account status and the time of your last sign-in.',
        'Profile data: an optional display name, avatar URL, short bio and country.',
        'Session data: a refresh-token hash, the user agent that created it, and its expiry. Sessions expire and are deleted automatically.',
        'Contest participation: which contests you entered, your answers, your score, your rank and your prize.',
        'Payment data: the amount and asset invoiced, the deposit address reserved for your entry, the blockchain transaction hash, the number of confirmations, and the payment status.',
        'Payout data: a wallet address you have saved as a withdrawal destination, and the transaction hash of any payout we send to it.',
        'Abuse-prevention data: request counts used for rate limiting, keyed by an identifier derived from your account or IP address.',
      ],
    },
    {
      heading: '3. What we never collect or store',
      bullets: [
        'We do not store card numbers, bank details or fiat payment credentials.',
        'We do not store private keys, seed phrases or mnemonics. There is no column for them in our database, and deposit-wallet keys are held offline by the operator.',
        'We do not ask for, or store, any password other than your ITA6 password.',
      ],
    },
    {
      heading: '4. Blockchain data is public',
      paragraphs: [
        'USDT transfers happen on a public blockchain. If you pay a deposit address, that transaction — its amount, its sender, its recipient and its timestamp — is permanent and publicly visible to anyone, including us. We cannot delete it, alter it or hide it, and neither can you. Only ever send from a wallet you control and understand to be appropriate for this purpose.',
        'We record the transaction hash of deposits and payouts so that payments can be verified and disputes resolved.',
      ],
    },
    {
      heading: '5. Third-party services',
      bullets: [
        'Blockchain data providers: we query public blockchain APIs (for example TronGrid and Etherscan) to detect and verify transfers. The deposit address is sent to these providers as part of a normal address lookup.',
        'Cloud hosting: the application runs on Cloudflare Workers with a Cloudflare D1 database.',
        'Payout signing: where automated payouts are enabled, a signing service controlled by the operator receives the transaction details but never discloses your data to third parties.',
      ],
    },

    {
      heading: '6. Why we process it',
      bullets: [
        'To operate your account, run contests and pay prizes.',
        'To verify payments independently, including checking the token, the network, the destination, the amount and the number of confirmations.',
        'To prevent fraud, abuse and duplicate settlement.',
        'To meet our accounting and audit obligations.',
      ],
    },
    {
      heading: '7. How long we keep it',
      bullets: [
        'Account and participation records are kept while your account exists.',
        'Financial records (payments, payouts and ledger entries) are append-only and are retained after account closure, because they are an accounting record of real money movements.',
        'Expired sessions, elapsed rate-limit windows and stale idempotency keys are deleted automatically by scheduled maintenance.',
      ],
    },
    {
      heading: '8. Your choices',
      paragraphs: [
        'You can update your profile, change your display name and manage your saved withdrawal addresses from your account. To request access to, or deletion of, your account data, contact support. Deletion removes your personal data but cannot remove financial records or anything already written to a public blockchain.',
      ],
    },
    {
      heading: '9. Security',
      paragraphs: [
        'Passwords are stored as PBKDF2 hashes. Sessions use signed, HttpOnly cookies. Administrative actions require an administrator account and are recorded in an audit log. Payment confirmation is decided server-side only: a browser claiming success cannot mark anything paid, and a payout is only reported complete once the blockchain transaction is confirmed.',
      ],
    },
    {
      heading: '10. Contact',
      paragraphs: [
        'Questions about this policy can be sent to the support address published on the site.',
      ],
    },
  ],
};

export const TERMS_AND_CONDITIONS: LegalDocument = {
  titleKey: 'legal.terms',
  lastUpdated: LAST_UPDATED,
  intro:
    'These terms govern your use of ITA6. By creating an account or entering a contest you agree to them.',
  sections: [
    {
      heading: '1. Accounts',
      bullets: [
        'You must provide a working email address and keep your password secret.',
        'One account per person. Accounts may not be shared, sold or transferred.',
        'You are responsible for everything done through your account.',
      ],
    },
    {
      heading: '2. Contests',
      bullets: [
        'Contests are skill-based. Results depend on the correctness of your answers and, where contestants tie, on the time you took.',
        'Contest timing is decided by the server, not by your device. Starting, answering and finishing are only accepted inside the contest window.',
        'Each question may be answered once. Answers are scored on the server when the contest is finalized.',
        'Entry fees, prize pools, the number of paid places and the schedule are shown on the contest page before you enter.',
      ],
    },
    {
      heading: '3. Payments in crypto',
      bullets: [
        'Entry is paid in USDT on the network named on the contest. Each entry is given its own deposit address.',
        'Send the exact amount, in the stated token, on the stated network, to the address shown. Sending a different token, a different network or a wrong amount can result in funds that we cannot credit to your entry.',
        'A payment is only accepted once our systems have independently verified the transfer and the required number of confirmations has been reached. A payment showing in your wallet is not the same as a payment accepted by ITA6.',
        'Deposit addresses are reserved for a single entry and expire. Do not reuse an expired address.',
        'Network fees are paid by you when you deposit. We do not deduct fees from prize amounts.',
      ],
    },
    {
      heading: '4. Prizes and payouts',
      bullets: [
        'Prize allocation is computed by the server from the final standings and is deterministic: the same results always produce the same allocation.',
        'Payouts are sent to the wallet address you provide. You are responsible for providing a correct address on the correct network. Transfers to a wrong or incompatible address cannot be recovered.',
        'A payout is reported as paid only once its blockchain transaction is confirmed. We never mark a payout as paid before the transaction exists.',
        'Where a payout failure is caused by information you supplied, such as an incorrect address, we cannot reverse the transaction.',
      ],
    },
    {
      heading: '5. Fair play',
      paragraphs: [
        'You must not interfere with a contest, submit answers on behalf of another person, automate your participation, or exploit a fault in the platform. We may disqualify an entry, withhold a prize or suspend an account where we have reasonable grounds to believe this has happened.',
      ],
    },
    {
      heading: '6. Availability and changes',
      paragraphs: [
        'We may add, change or withdraw contests, features and payment rails. Where a contest cannot take place, entry fees for that contest are refunded to the address you provide. Crypto is currently the only active payment method.',
      ],
    },
    {
      heading: '7. Liability',
      paragraphs: [
        'We provide the platform as it is. We are not responsible for losses caused by blockchain conditions outside our control, including network congestion, fees, reorganisation, or a transfer sent to an address you supplied incorrectly. Nothing in these terms limits liability that cannot lawfully be limited.',
      ],
    },
    {
      heading: '8. Disputes',
      paragraphs: [
        'Contact support first. We will look at the contest record, the payment record and the blockchain evidence, and explain the outcome in writing.',
      ],
    },
  ],
};

